import * as React from 'react';
import isEqual from 'react-fast-compare';

import { QueryParams, SetActiveIdCallback } from '@/pages/Notifications/notifications.types';
import { AlertTypes, DeliveryType } from '@/redux/api/alerts';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getBidderCountryAllowedToReceiveSMS } from '@/redux/modules/account/user/user.selectors';
import { openSMSConfirmationModal } from '@/redux/modules/modal';
import { getOpenModal } from '@/redux/modules/modal.selectors';
import {
    getSubscribedToSMSAlerts,
    getSubscriptionIdList,
    getSubscriptions,
    toggleSmsAlert,
} from '@/redux/modules/smsAlerts';
import {
    TrackSmsNotificationToggleEventTrigger,
    useTrackSmsNotificationToggle,
} from '@/utils/amplitude/analytics/notifications';

import AlertsRow from './Components/AlertsRow';
import Section from './Components/Section';
import StatusCheckbox from './Components/StatusCheckbox';

type Props = {
    activeId: QueryParams['section'];
    setActiveId: SetActiveIdCallback;
};

const Alerts = ({ activeId, setActiveId }: Props) => {
    const dispatch = useAppDispatch();
    const alerts = useAppSelector(getSubscriptionIdList).filter((alert) => alert !== 5);
    const openModal = useAppSelector(getOpenModal);
    const subscriptions = useAppSelector(getSubscriptions).filter((subscription) => subscription.alertTypeId !== 5);
    const hasSmsAlerts = useAppSelector(getSubscribedToSMSAlerts);
    const trackSmsNotificationToggle = useTrackSmsNotificationToggle();
    const smsAllowed = useAppSelector(getBidderCountryAllowedToReceiveSMS);

    const browserSubscriptions = subscriptions.filter(
        (subscription) => subscription.deliveryType === DeliveryType.browser
    );
    const emailSubscriptions = subscriptions.filter((subscription) => subscription.deliveryType === DeliveryType.email);
    const smsSubscriptions = subscriptions.filter((subscription) => subscription.deliveryType === DeliveryType.sms);

    if (!alerts.length) {
        return null;
    }

    return (
        <Section
            activeId={activeId}
            browser={
                <StatusCheckbox
                    array={browserSubscriptions}
                    category="auctions"
                    onChange={(checked) => {
                        dispatch(toggleSmsAlert(AlertTypes.All, DeliveryType.browser, !checked));
                    }}
                    selector={(subscription) => subscription.subscribed}
                    type="browser"
                />
            }
            component={alerts.map((id) => (
                <AlertsRow
                    alertTypeId={id}
                    key={`${id}${openModal}`}
                />
            ))}
            count={alerts.length}
            email={
                <StatusCheckbox
                    array={emailSubscriptions}
                    category="auctions"
                    onChange={(checked) => {
                        dispatch(toggleSmsAlert(AlertTypes.All, DeliveryType.email, !checked));
                    }}
                    selector={(subscription) => subscription.subscribed}
                    type="email"
                />
            }
            id="alerts"
            setActiveId={setActiveId}
            sms={
                smsAllowed && (
                    <StatusCheckbox
                        array={smsSubscriptions}
                        category="auctions"
                        key={openModal}
                        onChange={(checked) => {
                            if (hasSmsAlerts) {
                                dispatch(toggleSmsAlert(AlertTypes.All, DeliveryType.sms, !checked));

                                trackSmsNotificationToggle({
                                    auctionRemindersEnabled: false,
                                    eventTrigger: TrackSmsNotificationToggleEventTrigger.EmailsAndNotifications,
                                    outbidEnabled: false,
                                    paymentRemindersEnabled: false,
                                });
                            } else {
                                dispatch(openSMSConfirmationModal(AlertTypes.All));
                            }
                        }}
                        selector={(subscription) => subscription.subscribed}
                        type="sms"
                    />
                )
            }
        />
    );
};

export default React.memo<Props>(Alerts, isEqual);
